import type { AxiosInstance } from 'axios';

export interface CartResponse {
  id: string;
  userId: string | null;
  anonymousId: string | null;
  status: string;
  couponCode: string | null;
  couponDiscountPaise: number;
  /** Lines whose price moved since they were added; the cart already carries the new price. */
  priceChanges?: Array<{ variantId: string; title: string; fromPaise: number; toPaise: number }>;
  items: Array<{
    variantId: string;
    qty: number;
    unitPricePaise: number;
    lineSubtotalPaise: number;
    gstRate: number;
    taxPaise: number;
    vendorId: string;
    product: {
      id: string;
      title: string;
      slug: string;
      vendorName: string;
      vendorSlug: string;
      imageUrl: string | null;
    };
    variant: { id: string; sku: string; optionValues: Record<string, string>; mrpPaise: number };
  }>;
  vendorGroups: Array<{
    vendorId: string;
    vendorName: string;
    /** So a cart group's header can link through to that seller's store. */
    vendorSlug: string;
    items: Array<{ variantId: string; qty: number; lineSubtotalPaise: number }>;
    subtotalPaise: number;
  }>;
  summary: {
    itemCount: number;
    subtotalPaise: number;
    taxPaise: number;
    discountPaise: number;
    couponDiscountPaise: number;
    grandTotalPaise: number;
  };
}

export function cartEndpoints(http: AxiosInstance) {
  return {
    getCart: (anonymousId?: string) =>
      http
        .get<CartResponse>('/cart', { params: anonymousId ? { anonymousId } : undefined })
        .then((r) => r.data),

    addItem: (cartId: string, variantId: string, qty: number) =>
      http.post<CartResponse>(`/cart/${cartId}/items`, { variantId, qty }).then((r) => r.data),

    updateItem: (cartId: string, variantId: string, qty: number) =>
      http.patch<CartResponse>(`/cart/${cartId}/items/${variantId}`, { qty }).then((r) => r.data),

    removeItem: (cartId: string, variantId: string) =>
      http.delete<CartResponse>(`/cart/${cartId}/items/${variantId}`).then((r) => r.data),

    clearCart: (cartId: string) => http.delete<CartResponse>(`/cart/${cartId}`).then((r) => r.data),

    applyCoupon: (cartId: string, couponCode: string) =>
      http.post<CartResponse>(`/cart/${cartId}/coupon`, { couponCode }).then((r) => r.data),

    removeCoupon: (cartId: string) =>
      http.delete<CartResponse>(`/cart/${cartId}/coupon`).then((r) => r.data),

    mergeGuestCart: (anonymousId: string) =>
      http.post<CartResponse>('/cart/merge', { anonymousId }).then((r) => r.data),
  };
}

export type CartEndpoints = ReturnType<typeof cartEndpoints>;
